/**
 *
 * HTL (22-05-23)
 * This file handles the interaction with Picnic Proxy, and getting image and video data from Facebook's CDN.
 *
 */
// HELPERS
import add_brandlogo from "../../../../helpers/add_brandlogo";
import add_asset from "../../../../helpers/add_asset";
import create_readable_id from "../../../../helpers/create_readable_id";
// TYPES
import { ContextActions, ContextState, Slide } from "../../../../types/contextType";
import { SocialExtensionResult } from "../types";

interface ProxyType {
  data: SocialExtensionResult;
  aspectRatio: string;
  format: string;
  teamId: string;
  creative: ContextState;
  creativeDispatch: React.Dispatch<ContextActions>;
  notificationDispatch: (action: NotificationAction) => void;
  mediaLoadingDispatch: (action: MediaLoadingAction) => void;
}

interface MediaLoadingAction {
  type: string;
  payload: {
    status?: boolean;
    type?: string;
    message?: string;
    progress?: number;
  };
}

interface NotificationAction {
  type: string;
  payload: {
    header: string;
    message: string;
    type: string;
    autoDismiss: boolean;
  };
}

export async function uploadMediaAssetsWithProxy({ data, aspectRatio, format, teamId, creative, creativeDispatch, notificationDispatch, mediaLoadingDispatch }: ProxyType): Promise<SocialExtensionResult> {
  try {
    const backup = {
      format: "story",
      aspectRatio: "picnic",
    };
    /**
     * HTL (24-05-23)
     * Find starting point to start adding new slides. Current implementation uses the first slide that doesn't have any media assets.
     * For example, with the initial state, the slide.media.bodyCropped will be "", therefore, the below .find() method will return "1".
     * When we execute the "ADD_NEW_SLIDE" dispatch below, the "newFrameSlideNumber" will be 1 + i ==> 1 + 0, adding the new scraped media to slideNumber 1.
     * The downside to this approach is that if the user has 3 frames, frame 1 has an asset, and frame 3 has an asset, "2" will be the value of startingFrameNumber, and the media in slide 3 will be overwritten. At the time of writing, it was assumed to be such a minor use case, and not worth spending time developing a solution for.
     */
    const numberOfExistingSlides = creative.metadata.slides.filter((slide: Slide) => slide.media.id).length;
    const emptySlideExists = creative.metadata.slides.find((slide: Slide) => !slide.media.id);
    const startingFrameNumber = emptySlideExists?.slideNumber || numberOfExistingSlides + 1;
    /**
     * JA (09-10-23)
     * Handles frame duplication issue which occurs based on the existence of empty slides before a social fetch is made: https://www.notion.so/picnicmedia/Broken-SF-assets-when-empty-slides-are-present-09d239eeef4a48a8b28d514486ef7f76?pvs=4
     * Before a social fetch is made, check if an empty slide exists and if there are any slides with media assets existing, i.e. not a new creative and that empty slides exists.
     * The creative slides list is spliced based on the index of the first occurance of an empty slide which is then used as the starting point of where social fetch frame assets are appended to.
     */
    if (emptySlideExists && numberOfExistingSlides) {
      /**
       * JA (10-10-23)
       * If the starting frame number is 1, it means the REMOVE_SLIDE dispatch below will be looking to remove the first slide object in the list.
       * If a user switches between formats while this is the case, we want to ensure that the slide object is available before we run the REMOVE_SLIDE dispatch.
       */
      if (startingFrameNumber === 1) {
        creativeDispatch({
          type: "ADD_NEW_SLIDE",
          payload: { slideNumber: startingFrameNumber },
        });
      }

      [...creative.metadata.slides].splice(startingFrameNumber - 1).map((slide) => {
        creativeDispatch({
          type: "REMOVE_SLIDE",
          payload: {
            id: slide.id,
          },
        });
      });
    }

    /**
     * HTL (12-06-23)
     * Using Promise.allSettled to return error to user if one of the assets has failed, but not to cancel the entire opertation.
     * Error: Asset on frame X could not be uploaded
     * Cause: Video is over 30 seconds. Please manually reduce video length.
     */
    await Promise.allSettled(
      data.frames.map(async (frame, i) => {
        try {
          if (frame.url) {
            /**
             * HTL (17-05-23)
             * Append new slide to existing slides. If there is no media URL in the first slide, overwrite first slide.
             * "startingFrameNumber" finds the first frame with an empty media URL then sets each new frame from there.
             */
            const newFrameSlideNumber = startingFrameNumber + i;
            let proxyFile = await fetchMediaAssetWithProxy(frame.url);
            /**
             * HTL (01-06-23)
             * If there are no existing slides, i.e. the first slide number is 1. We don't want to add another first frame.
             * We want to add a new slide for every subsequent slide after the first slide.
             */
            if (newFrameSlideNumber !== 1) {
              creativeDispatch({
                type: "ADD_NEW_SLIDE",
                payload: { slideNumber: newFrameSlideNumber },
              });
            }

            /**
             * HTL (26-06-23)
             * Add the frame.title as slide content to each new slide.
             */
            creativeDispatch({
              type: "UPDATE_CREATIVE_METADATA_SLIDE_CONTENT",
              payload: { id: newFrameSlideNumber, content: frame.title },
            });

            const assetUploadedStatus = await add_asset({
              file: proxyFile.body,
              slideNumber: newFrameSlideNumber,
              formatType: format || backup.format,
              aspectRatio: aspectRatio || backup.aspectRatio,
              teamId,
              creativeId: creative.creativeId,
              creativeDispatch,
              mimeType: proxyFile.mimeType,
              name: create_readable_id(4),
              mediaLoadingDispatch,
            });

            return assetUploadedStatus;
          }
        } catch (error) {
          /**
           * HTL (13-06-23)
           * If an error occurs, the slide will still be created, we can't remove the slide here because we don't have access to the slide.id, the necessary payload for REMOVE_SLIDE dispatch type.
           */
          notificationDispatch({
            type: "ADD",
            payload: {
              header: error.message,
              message: error.cause,
              type: "warning",
              autoDismiss: true,
            },
          });
        }
      }),
    );
    if (data.brand.profilePictureUrl) {
      const proxyFileLogo = await fetchMediaAssetWithProxy(data.brand.profilePictureUrl);
      await add_brandlogo({
        file: proxyFileLogo.body,
        formatType: creative.metadata.format || backup.format,
        teamId,
        creativeId: creative.creativeId,
        creativeDispatch,
        mimeType: proxyFileLogo.mimeType,
        name: create_readable_id(4),
        notificationDispatch,
        mediaLoadingDispatch,
      });
    }
    /**
     * HTL (13-06-23)
     * The response from the Promise.allSettled will give the returned array of promises a status value of "fulfilled" or "rejected".
     * [{"status": "fulfilled", "value": "success"}, {"status": "rejected", "reason": {}}]
     * In the add_asset helper, we are throwing the error so it can be handled by our map here, showing a notification if there is one error out of x, and not stopping the whole process.
     */
    return data;
  } catch (error) {
    throw new Error(error);
  }
}

export async function fetchMediaAssetWithProxy(url: string | undefined): Promise<any> {
  try {
    let res = await fetch(`https://proxy.picnic-media.com/${url}`);
    let mediaAsset = await res.blob();
    let response = {
      body: mediaAsset,
      mimeType: mediaAsset.type,
    };
    return response;
  } catch (error) {
    throw new Error(error);
  }
}
